import { FC, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Table from '@/components/Tables/Table/Table';
import { Modifier } from '@/types/menus.types';
import { useSTKContext } from '@/context/context';
import { formatModifierPriceCell } from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';
import styles from './ProductsTable.module.css';

interface ModifiersTableProps {
  storeModifiers: Modifier[];
  modifierCategory: string;
  setPriceDeviationsData: (data: {
    isPriceDeviationsDrawerOpen: boolean;
    modifierCategory: string;
    modifierKeys: {
      modifierId: string;
      slotId: string;
      modifierWeightId: string;
      variantId: string;
    }[];
    selectedGroupName: string;
    cameFromEditDrawer: boolean;
  }) => void;
}

interface ModifierRow {
  modifierName: string;
  modifierDescription: string;
  modifierId: string;
  slotId: string;
  modifierWeightId: string;
  variantId?: string;
  [key: string]: unknown;
}

const ModifiersTable: FC<ModifiersTableProps> = ({ storeModifiers, modifierCategory, setPriceDeviationsData }) => {
  const { t } = useTranslation();
  const { storesAndGroups } = useSTKContext();
  const inactiveLabel = t('common.inactive');

  const allStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }

    if (storesAndGroups.origin === 'tab-store') {
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet);
    }

    if (storesAndGroups.origin === 'tab-stores-groups') {
      return storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || [];
    }

    return [];
  }, [storesAndGroups]);

  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;

      if (!storesAndGroups) {
        return { count: 0, total: 0 };
      }

      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count || 0;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length || 0;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount || 0;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length || 0;
      }

      return { count: count || 0, total: total || 0 };
    },
    [storesAndGroups],
  );

  const tableData = useMemo(() => {
    if (!storeModifiers || storeModifiers.length === 0) {
      return [];
    }

    return storeModifiers.map((modifier) => {
      const row: ModifierRow = {
        modifierName: modifier.modifierName || '',
        modifierDescription: modifier.modifierDescription || '',
        modifierId: modifier.modifierId,
        slotId: modifier.slotId,
        modifierWeightId: modifier.modifierWeightId,
        variantId: modifier.variantId,
      };

      allStoreGroupNames.forEach((storeGroupName) => {
        const storeGroupData = modifier.storeGroups?.find((sg) => sg.storeGroupName === storeGroupName);

        if (!storeGroupData || !storeGroupData.chosenPriceRange) {
          row[storeGroupName] = inactiveLabel;
        } else {
          const { text, showRangeInfo } = formatModifierPriceCell(storeGroupData.chosenPriceRange, inactiveLabel);

          row[storeGroupName] = {
            display: text,
            hasRange: showRangeInfo,
            groupName: storeGroupName,
            modifierId: modifier.modifierId,
            slotId: modifier.slotId,
            modifierWeightId: modifier.modifierWeightId,
            variantId: modifier.variantId,
          };
        }
      });

      return row;
    });
  }, [storeModifiers, allStoreGroupNames, inactiveLabel]);

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        id: 'modifierName',
        accessorKey: 'modifierName',
        header: 'MODIFIER',
        cell: (info: any) => (
          <div className={styles.productColumnContent}>
            <div className={styles.productName}>{info.getValue()}</div>
            <div className={styles.productDescription}>{info.row.original.modifierDescription}</div>
          </div>
        ),
        size: 350,
        minSize: 250,
        maxSize: 450,
      },
    ];

    allStoreGroupNames.forEach((storeGroupName, index) => {
      const { count, total } = getStoreGroupsCount(storeGroupName);

      cols.push({
        id: `${storeGroupName}_${index}`,
        accessorKey: storeGroupName,
        header: () => (
          <div className={styles.headerWithCount}>
            <div className={styles.headerName}>{storeGroupName.toUpperCase()}</div>
            <div className={styles.headerCount}>
              {count} / {total}
            </div>
          </div>
        ),
        size: 145,
        minSize: 130,
        maxSize: 160,
        cell: (info: any) => {
          const cellData = info.getValue();

          if (typeof cellData === 'string') {
            return <span className={styles.inactive}>{cellData}</span>;
          }

          if (!cellData) {
            return <span className={styles.inactive}>{inactiveLabel}</span>;
          }

          if (cellData.hasRange) {
            return (
              <div className={styles.priceWithIcon}>
                <span>{cellData.display}</span>
                <InfoOutlinedIcon
                  className={styles.infoIcon}
                  onClick={() => {
                    if (cellData.variantId && cellData.groupName) {
                      setPriceDeviationsData({
                        selectedGroupName: cellData.groupName,
                        modifierCategory,
                        modifierKeys: [
                          {
                            modifierId: cellData.modifierId,
                            slotId: cellData.slotId,
                            modifierWeightId: cellData.modifierWeightId,
                            variantId: cellData.variantId,
                          },
                        ],
                        isPriceDeviationsDrawerOpen: true,
                        cameFromEditDrawer: true,
                      });
                    }
                  }}
                  sx={{
                    width: '18px',
                    height: '18px',
                    color: '#266DCC',
                    cursor: 'pointer',
                    marginLeft: '4px',
                  }}
                />
              </div>
            );
          }

          return <span>{cellData.display}</span>;
        },
      });
    });

    return cols;
  }, [allStoreGroupNames, getStoreGroupsCount, inactiveLabel, modifierCategory, setPriceDeviationsData]);

  return (
    <div className={styles.container}>
      <div className={styles.tableWrapper}>
        <Table data={tableData} columns={columns} height="calc(85vh - 350px)" />
      </div>
    </div>
  );
};

export default ModifiersTable;
